/**
 * Plans how to mutate a Word paragraph when applying approved clause text.
 * Prefers local (partial) span replaces so unchanged formatting stays intact.
 */

export type MutationOp = {
  readonly kind: "replace" | "delete" | "insert";
  /** Text to find in the current paragraph (empty for insert). */
  readonly oldSnippet: string;
  /** Replacement / inserted text (empty for delete). */
  readonly newSnippet: string;
  /** Start offset in normalized old text (for ordering / disambiguation). */
  readonly start: number;
  /** End offset in normalized old text (exclusive). */
  readonly end: number;
  /**
   * For inserts: unique-ish text immediately before the insert point in the
   * current paragraph. Used to anchor Word.search.
   */
  readonly anchorBefore?: string;
};

export type ParagraphMutationPlan =
  | { readonly mode: "noop" }
  | { readonly mode: "partial"; readonly ops: readonly MutationOp[] }
  | { readonly mode: "full"; readonly reason: string };

const MAX_PARTIAL_OPS = 3;
/** Changed chars / max(old,new) above this → full replace. */
const MAX_CHANGED_RATIO = 0.45;
/** Prefer searchable snippets at least this long (except tiny pure deletes). */
const MIN_SEARCH_SNIPPET = 8;
const MAX_SEARCH_SNIPPET = 180;
const MIN_ANCHOR_LEN = 12;

export const normalizeParagraphWs = (s: string): string =>
  s.replace(/\s+/g, " ").trim();

/**
 * Fold typographic quotes/apostrophes so curly vs straight differences do not
 * explode the diff into many 1-char ops (common Word vs AI payload mismatch).
 */
export const foldTypographicPunctuation = (ch: string): string => {
  switch (ch) {
    case "\u201C": // “
    case "\u201D": // ”
    case "\u201E": // „
    case "\u201F": // ‟
    case "\u00AB": // «
    case "\u00BB": // »
      return '"';
    case "\u2018": // ‘
    case "\u2019": // ’
    case "\u201A": // ‚
    case "\u201B": // ‛
    case "\u2032": // ′
    case "`":
      return "'";
    default:
      return ch;
  }
};

type NormMap = {
  readonly normalized: string;
  /** For each index in normalized, corresponding start index in original. */
  readonly toOrig: readonly number[];
};

/**
 * Collapse whitespace to single spaces and fold typographic punctuation while
 * mapping normalized indices back to the original string.
 */
export const buildNormMap = (raw: string): NormMap => {
  const toOrig: number[] = [];
  let out = "";
  let i = 0;
  const n = raw.length;
  while (i < n && /\s/.test(raw[i]!)) {
    i += 1;
  }
  while (i < n) {
    const ch = raw[i]!;
    if (/\s/.test(ch)) {
      while (i < n && /\s/.test(raw[i]!)) {
        i += 1;
      }
      if (i < n) {
        out += " ";
        // Point at the last whitespace char before the next token.
        toOrig.push(i - 1);
      }
      continue;
    }
    out += foldTypographicPunctuation(ch);
    toOrig.push(i);
    i += 1;
  }
  return { normalized: out, toOrig };
};

const sliceOrig = (raw: string, map: NormMap, start: number, end: number): string => {
  if (end <= start) {
    return "";
  }
  const oStart = map.toOrig[start];
  if (oStart == null) {
    return "";
  }
  let oEnd: number;
  if (end >= map.normalized.length) {
    oEnd = raw.length;
  } else {
    const next = map.toOrig[end];
    oEnd = next == null ? raw.length : next;
  }
  return raw.slice(oStart, oEnd);
};

const clipSnippet = (s: string): string => {
  if (s.length <= MAX_SEARCH_SNIPPET) {
    return s;
  }
  return s.slice(0, MAX_SEARCH_SNIPPET);
};

const commonPrefixLen = (a: string, b: string): number => {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a[i] === b[i]) {
    i += 1;
  }
  return i;
};

const commonSuffixLen = (a: string, b: string, aStart: number, bStart: number): number => {
  let i = 0;
  while (
    i < a.length - aStart &&
    i < b.length - bStart &&
    a[a.length - 1 - i] === b[b.length - 1 - i]
  ) {
    i += 1;
  }
  return i;
};

type MatchBlock = { readonly a: number; readonly b: number; readonly size: number };

const findLongestMatch = (
  a: string,
  aLo: number,
  aHi: number,
  b: string,
  bLo: number,
  bHi: number,
): MatchBlock => {
  let bestI = aLo;
  let bestJ = bLo;
  let bestSize = 0;
  if (aHi <= aLo || bHi <= bLo) {
    return { a: aLo, b: bLo, size: 0 };
  }
  const indexes = new Map<string, number[]>();
  for (let j = bLo; j < bHi; j += 1) {
    const ch = b[j]!;
    const list = indexes.get(ch);
    if (list) {
      list.push(j);
    } else {
      indexes.set(ch, [j]);
    }
  }
  let j2len = new Map<number, number>();
  for (let i = aLo; i < aHi; i += 1) {
    const next = new Map<number, number>();
    const hits = indexes.get(a[i]!) ?? [];
    for (const j of hits) {
      const size = (j2len.get(j - 1) ?? 0) + 1;
      next.set(j, size);
      if (size > bestSize) {
        bestSize = size;
        bestI = i - size + 1;
        bestJ = j - size + 1;
      }
    }
    j2len = next;
  }
  return { a: bestI, b: bestJ, size: bestSize };
};

const matchingBlocks = (a: string, b: string): MatchBlock[] => {
  const blocks: MatchBlock[] = [];
  const queue: Array<[number, number, number, number]> = [
    [0, a.length, 0, b.length],
  ];
  while (queue.length > 0) {
    const [aLo, aHi, bLo, bHi] = queue.pop()!;
    const m = findLongestMatch(a, aLo, aHi, b, bLo, bHi);
    if (m.size < 1) {
      continue;
    }
    blocks.push(m);
    if (aLo < m.a && bLo < m.b) {
      queue.push([aLo, m.a, bLo, m.b]);
    }
    if (m.a + m.size < aHi && m.b + m.size < bHi) {
      queue.push([m.a + m.size, aHi, m.b + m.size, bHi]);
    }
  }
  blocks.sort((x, y) => x.a - y.a || x.b - y.b);
  const merged: MatchBlock[] = [];
  for (const blk of blocks) {
    const last = merged[merged.length - 1];
    if (
      last &&
      last.a + last.size === blk.a &&
      last.b + last.size === blk.b
    ) {
      merged[merged.length - 1] = {
        a: last.a,
        b: last.b,
        size: last.size + blk.size,
      };
    } else {
      merged.push(blk);
    }
  }
  return merged;
};

type Opcode = {
  readonly tag: "equal" | "replace" | "delete" | "insert";
  readonly a0: number;
  readonly a1: number;
  readonly b0: number;
  readonly b1: number;
};

const getOpcodes = (a: string, b: string): Opcode[] => {
  const blocks = matchingBlocks(a, b);
  const ops: Opcode[] = [];
  let i = 0;
  let j = 0;
  for (const blk of blocks) {
    if (i < blk.a || j < blk.b) {
      if (i < blk.a && j < blk.b) {
        ops.push({ tag: "replace", a0: i, a1: blk.a, b0: j, b1: blk.b });
      } else if (i < blk.a) {
        ops.push({ tag: "delete", a0: i, a1: blk.a, b0: j, b1: j });
      } else {
        ops.push({ tag: "insert", a0: i, a1: i, b0: j, b1: blk.b });
      }
    }
    i = blk.a + blk.size;
    j = blk.b + blk.size;
    ops.push({
      tag: "equal",
      a0: blk.a,
      a1: i,
      b0: blk.b,
      b1: j,
    });
  }
  if (i < a.length || j < b.length) {
    if (i < a.length && j < b.length) {
      ops.push({ tag: "replace", a0: i, a1: a.length, b0: j, b1: b.length });
    } else if (i < a.length) {
      ops.push({ tag: "delete", a0: i, a1: a.length, b0: j, b1: j });
    } else {
      ops.push({ tag: "insert", a0: i, a1: i, b0: j, b1: b.length });
    }
  }
  return ops;
};

const changedCharCount = (ops: readonly Opcode[]): number => {
  let n = 0;
  for (const op of ops) {
    if (op.tag === "equal") {
      continue;
    }
    n += Math.max(op.a1 - op.a0, op.b1 - op.b0);
  }
  return n;
};

const trySingleMiddleOp = (
  oldText: string,
  newText: string,
  oldMap: NormMap,
  newMap: NormMap,
): ParagraphMutationPlan | null => {
  const a = oldMap.normalized;
  const b = newMap.normalized;
  const prefix = commonPrefixLen(a, b);
  const suffix = commonSuffixLen(a, b, prefix, prefix);
  const a0 = prefix;
  const a1 = a.length - suffix;
  const b0 = prefix;
  const b1 = b.length - suffix;
  if (a0 > a1 || b0 > b1) {
    return null;
  }
  const oldMid = a.slice(a0, a1);
  const newMid = b.slice(b0, b1);
  if (oldMid === newMid) {
    return { mode: "noop" };
  }

  const changed = Math.max(oldMid.length, newMid.length);
  const denom = Math.max(a.length, b.length, 1);
  if (changed / denom > MAX_CHANGED_RATIO) {
    return null;
  }

  if (oldMid.length === 0 && newMid.length > 0) {
    const anchorBefore = clipSnippet(sliceOrig(oldText, oldMap, Math.max(0, a0 - 40), a0));
    if (normalizeParagraphWs(anchorBefore).length < MIN_ANCHOR_LEN) {
      return null;
    }
    return {
      mode: "partial",
      ops: [
        {
          kind: "insert",
          oldSnippet: "",
          newSnippet: sliceOrig(newText, newMap, b0, b1),
          start: a0,
          end: a0,
          anchorBefore,
        },
      ],
    };
  }

  if (newMid.length === 0 && oldMid.length > 0) {
    const oldSnippet = clipSnippet(sliceOrig(oldText, oldMap, a0, a1));
    if (oldSnippet.trim().length < 1) {
      return null;
    }
    return {
      mode: "partial",
      ops: [
        {
          kind: "delete",
          oldSnippet,
          newSnippet: "",
          start: a0,
          end: a1,
        },
      ],
    };
  }

  // Expand a short middle replace to include a bit of unique context so Word.search is reliable.
  let expandLeft = 0;
  let expandRight = 0;
  if (oldMid.length < MIN_SEARCH_SNIPPET) {
    expandLeft = Math.min(a0, MIN_SEARCH_SNIPPET - oldMid.length);
    const need = MIN_SEARCH_SNIPPET - oldMid.length - expandLeft;
    expandRight = Math.min(a.length - a1, Math.max(0, need));
  }
  const ea0 = a0 - expandLeft;
  const ea1 = a1 + expandRight;
  const eb0 = b0 - expandLeft;
  const eb1 = b1 + expandRight;
  const oldSnippet = clipSnippet(sliceOrig(oldText, oldMap, ea0, ea1));
  const newSnippet = sliceOrig(newText, newMap, eb0, eb1);
  if (oldSnippet.trim().length < MIN_SEARCH_SNIPPET) {
    return null;
  }
  return {
    mode: "partial",
    ops: [
      {
        kind: "replace",
        oldSnippet,
        newSnippet,
        start: ea0,
        end: ea1,
      },
    ],
  };
};

const opcodesToPartialPlan = (
  oldText: string,
  newText: string,
  oldMap: NormMap,
  newMap: NormMap,
  opcodes: readonly Opcode[],
): ParagraphMutationPlan => {
  const unequal = opcodes.filter((o) => o.tag !== "equal");
  const a = oldMap.normalized;
  const b = newMap.normalized;
  const denom = Math.max(a.length, b.length, 1);
  const ratio = changedCharCount(opcodes) / denom;
  if (ratio > MAX_CHANGED_RATIO) {
    return { mode: "full", reason: "changed_ratio" };
  }
  if (unequal.length > MAX_PARTIAL_OPS) {
    return { mode: "full", reason: "too_many_ops" };
  }

  const ops: MutationOp[] = [];
  for (const op of unequal) {
    if (op.tag === "replace") {
      let a0 = op.a0;
      let a1 = op.a1;
      let b0 = op.b0;
      let b1 = op.b1;
      const midLen = a1 - a0;
      if (midLen < MIN_SEARCH_SNIPPET) {
        const expandLeft = Math.min(a0, MIN_SEARCH_SNIPPET - midLen);
        const need = MIN_SEARCH_SNIPPET - midLen - expandLeft;
        const expandRight = Math.min(a.length - a1, Math.max(0, need));
        a0 -= expandLeft;
        a1 += expandRight;
        b0 -= expandLeft;
        b1 += expandRight;
      }
      const oldSnippet = clipSnippet(sliceOrig(oldText, oldMap, a0, a1));
      const newSnippet = sliceOrig(newText, newMap, b0, b1);
      if (oldSnippet.trim().length < MIN_SEARCH_SNIPPET) {
        return { mode: "full", reason: "short_replace_snippet" };
      }
      ops.push({
        kind: "replace",
        oldSnippet,
        newSnippet,
        start: a0,
        end: a1,
      });
    } else if (op.tag === "delete") {
      const oldSnippet = clipSnippet(sliceOrig(oldText, oldMap, op.a0, op.a1));
      if (oldSnippet.trim().length < 1) {
        continue;
      }
      ops.push({
        kind: "delete",
        oldSnippet,
        newSnippet: "",
        start: op.a0,
        end: op.a1,
      });
    } else {
      const newSnippet = sliceOrig(newText, newMap, op.b0, op.b1);
      if (newSnippet.length === 0) {
        continue;
      }
      const anchorBefore = clipSnippet(
        sliceOrig(oldText, oldMap, Math.max(0, op.a0 - 40), op.a0),
      );
      if (normalizeParagraphWs(anchorBefore).length < MIN_ANCHOR_LEN) {
        return { mode: "full", reason: "insert_without_anchor" };
      }
      ops.push({
        kind: "insert",
        oldSnippet: "",
        newSnippet,
        start: op.a0,
        end: op.a0,
        anchorBefore,
      });
    }
  }

  if (ops.length === 0) {
    return { mode: "noop" };
  }
  if (ops.length > MAX_PARTIAL_OPS) {
    return { mode: "full", reason: "too_many_ops" };
  }
  return { mode: "partial", ops };
};

/**
 * Build a mutation plan comparing current paragraph text to approved text.
 */
export const planParagraphMutation = (
  oldText: string,
  newText: string,
): ParagraphMutationPlan => {
  const oldMap = buildNormMap(oldText);
  const newMap = buildNormMap(newText);
  const a = oldMap.normalized;
  const b = newMap.normalized;

  if (a.length === 0 && b.length === 0) {
    return { mode: "noop" };
  }
  if (a === b) {
    return { mode: "noop" };
  }
  if (a.length === 0 || b.length === 0) {
    return { mode: "full", reason: "empty_side" };
  }

  // Fast path: one contiguous middle edit (covers most AI clause tweaks).
  const middle = trySingleMiddleOp(oldText, newText, oldMap, newMap);
  if (middle) {
    return middle;
  }

  const opcodes = getOpcodes(a, b);
  const unequal = opcodes.filter((o) => o.tag !== "equal");
  if (unequal.length === 0) {
    return { mode: "noop" };
  }
  return opcodesToPartialPlan(oldText, newText, oldMap, newMap, opcodes);
};
